import { Storage } from 'aws-amplify';
import { getIn, useFormikContext, Form } from 'formik';
import { Link, useLocation } from 'react-router-dom';
import {
    Button,
    Box,
    Typography,
    Stepper,
    Step,
    StepLabel,
    Grid,
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Link as MuiLink } from '@mui/material';
import { AllocateDates } from '../../components/selectComponents/AllocateDates';
import { CurrencyDetails } from '../../components/assignPromotionFlowComponent/CurrencyDetails/CurrencyDetails.component';
import { InputComponent } from '../../components/inputComponents/InputComponent';
import { ParticipationLimits } from '../../components/assignPromotionFlowComponent/ParticipationLimits';
import { AdditionalPromoDetails } from '../../components/assignPromotionFlowComponent/AdditionalPromoDetails/AdditionalPromoDetails';
import { SavePromotion } from '../../components/assignPromotionFlowComponent/SavePromotion';
import { LuckyNumbersBatchDetails } from '../../components/assignPromotionFlowComponent/LuckyNumbersBatchDetails';
import { SelectMarketLanguage } from '../../components/assignPromotionFlowComponent/SelectMarketLanguage';
import { SelectOption } from '../../components/selectComponents/SelectOption';
import ProofOfPurchaseDetails from '../../../src/pages/EditPromotion/ProofOfPurchase/ProofOfPurchaseDetails';
import { FriendReferralForm } from './../EditPromotion/FriendReferral/FriendReferralForm';
import { TooltipText } from '../../components/inputComponents/TooltipText';
import { usePrompt } from '../../common/PromptOnChanges/promptHelper';
import {
    mechanicOptions,
    flowLabelMap,
    flowLambdas,
    languageCodes,
} from '../../constants/lists';
import moment from 'moment-timezone';
import { timezones } from '../../constants/timezones';
import { ParticipationSettings } from '../../components/assignPromotionFlowComponent/ParticipationSettings';
import { useEffect, useState } from 'react';
import {
    PromoMechanic,
    PromoVariation,
} from '../../types/componentTypes/editPromotion';
import {
    AssignPromoFormProps,
    ICreatePromotionState,
} from '../../types/componentTypes/assignPromo';
import { commonLinks } from '../../constants/helpful-link';
import { AGE_LIMIT_LAMBDA } from '../../types/componentTypes/limits';
import { EnableDoubleWinToggle } from '../../components/inputComponents/EnableDoubleWinToggle';
import { deleteKeys } from '../EditPromotion/LimitsTab/helpers/limit-helpers';
import { PARTICIPATION_LIMIT_SCHEMA } from '../EditPromotion/LimitsTab/helpers/limit-schemas';
import { Notification } from '../../common/Notification/Notification';
import { defaultNotifState } from '../../constants/currency-constants';

const uniqid = require('uniqid');

Storage.configure({
    customPrefix: {
        public: '',
        protected: '',
        private: '',
    },
});

export const promoIdCharLength: number = 14;

type stepTypes = {
    [key: number]: string[];
};
const stepFields: stepTypes = {
    0: ['configurationParameters.additionalInformation.name'],
    1: ['configurationParameters.country', 'configurationParameters.language'],
    2: ['tempParams.startDateTime', 'tempParams.endDateTime'],
    3: ['tempParams.flowOption'],
    4: [
        'params.reduceAmount',
        'tempParams.currencyNames',
        'tempParams.leaderboadCurrencyNames',
        'leaderboard.expiresAt',
        'tempParams.luckyNumbersBatchSize',
        'tempParams.luckyNumbersBatchAmount',
    ],
    5: [
        'secrets.mixCodesParameters',
        'params.lotIds',
        'params.campaignIds',
        'pluginParams.emailTemplateId',
        'pluginParams.url',
        'pluginParams.rejectedEmailTemplateId',
        'pluginRoutes.processReceipts.externalReqParams.timeoutInMillis',
        'pluginRoutes.processReceipts.externalReqParams.scannerConfigInstanceId',
    ],
    6: [
        'participationLimits.participationLimit',
        'participationLimits.participationLimitTime',
        'participationLimits.participationLifetimeLimit',
        'participationLimits.minAge',
        'participationLimits.participationLimitCalendarDatesRange.startDate',
        'participationLimits.participationLimitCalendarDatesRange.endDate',
        'participationLimits.participationLimitStartEndDatesRange.startDate',
        'participationLimits.participationLimitStartEndDatesRange.endDate',
        'participationLimits.participationCalendarCycleLimit',
        'allocationRules',
    ],
    7: [
        'referralRewards.referrer.currency',
        'referralRewards.referrer.amount',
        'referralRewards.referee.currency',
        'referralRewards.referee.amount',
        'referralRewards.emailTemplateId',
    ],
};

const subMechanicDefaults = {
    'Instant Win': 'Instant Win (Winning Moments)',
    'Prize Draw (Lottery)': 'Prize Draw (Lottery)',
    'Collect & Get': 'Collect & Get',
};

function AssignPromotionForm({
    step,
    updateStep,
    campaignMarkets,
}: AssignPromoFormProps) {
    const formik = useFormikContext<ICreatePromotionState>();
    const [steps, setSteps] = useState([
        'Promotion Name',
        'Market & Language',
        'Timeline',
        'Promotion Mechanic',
        'Currency Details',
        'Proof of Purchase',
        'Participation Limits',
        'Referral Rewards',
    ]);
    const [formHasErrors, setFormHasErrors] = useState(false);
    const [completed, setCompleted] = useState(false);
    const [notificationState, setNotificationState] = useState(defaultNotifState);

    const { setFieldValue, errors } = formik;

    useEffect(() => {
        if (errors && Object.values(errors).length) {
            setFormHasErrors(true);
            return;
        }
        return setFormHasErrors(false);
    }, [errors]);

    const isLuckyNumbers = formik.values.tempParams.flowOption === 'Lucky Numbers';

    useEffect(() => {
        setCompleted(false);
    }, [formik.values.tempParams.flowOption]);

    const validateAndMove = async () => {
        const fieldsToValidate =
            isLuckyNumbers && step === 7 ? [] : (stepFields[step] || []);
        fieldsToValidate.forEach((fieldName) => {
            formik.setFieldTouched(fieldName, true);
        });

        const validation = await formik.validateForm();
        let result = true;
        fieldsToValidate.forEach((fieldName) => {
            if (getIn(validation, fieldName)) {
                result = false;
            }
        });
        if (result) {
            updateStep(1);
        }
    };

    useEffect(() => {
        const mainSteps = [
            'Promotion Name',
            'Market & Language',
            'Timeline',
            'Promotion Mechanic',
            'Currency Details',
            'Proof of Purchase',
            'Participation Limits',
            'Referral Rewards',
            'Additional Details',
        ];
        if (formik.values.tempParams.flowOption === 'Collect & Get') {
            const changedSteps = [...mainSteps];
            const stepIndex = changedSteps.indexOf('Participation Limits');
            changedSteps[stepIndex] = 'Participation settings';
            setSteps(changedSteps);
        } else if (isLuckyNumbers) {
            const changedSteps = mainSteps
                .filter((label) => label !== 'Referral Rewards')
                .map((label) =>
                    label === 'Currency Details' ? 'Lucky Numbers Batch Details' : label
                );
            setSteps(changedSteps);
        } else {
            setSteps(mainSteps);
        }
    }, [formik.values.tempParams.flowOption, isLuckyNumbers]);

    const setFlow = (e: any, params: { option: string }) => {
        const selectedMechanic = params.option;
        const flow = flowLabelMap[selectedMechanic] || '';
        setFieldValue('flowLambdas', flowLambdas[flow]);
        setFieldValue('flow', flow);
        const subMechanic =
            subMechanicDefaults[
            selectedMechanic as keyof typeof subMechanicDefaults
            ] || '';
        setFieldValue(
            'configurationParameters.configurationSubMechanic',
            subMechanic
        );
    };

    const checkForAWFlag = (
        e: React.MouseEvent<HTMLInputElement>,
        params: { option: string }
    ) => {
        if (params.option === PromoVariation.InstantWinWM) {
            setFieldValue('params.alwaysWin', false);
            setFieldValue('params.algorithm', 'winningMoments');
        }

        if (params.option === PromoVariation.InstantWinAW) {
            setFieldValue('params.alwaysWin', true);
            setFieldValue('params.algorithm', 'winningMoments');
        }

        if (params.option === PromoVariation.InstantWinWMGP) {
            setFieldValue('params.alwaysWin', true);
            setFieldValue('params.algorithm', 'winningMoments');
        }
    };

    const convertDatesToTimestamp = (dateFields: Array<string>) => {
        dateFields.forEach((field) => {
            const date = getIn(formik.values, field);
            if (date && !Number.isInteger(date)) {
                setFieldValue(field, moment(date).valueOf(), false);
            }
        });
    };

    const mapTimeZones = (tzFields: Array<string>) => {
        tzFields.forEach((field) => {
            const tz = getIn(formik.values, field);
            if (tz) {
                setFieldValue(
                    field,
                    Object.keys(timezones).find((key) => timezones[key] === tz) ||
                    'Europe/London',
                    false
                );
            }
        });
    };

    const handleParticipationLimits = () => {
        const limitStates = formik.values.tempParams.limits;
        const participationLimits = formik.values.participationLimits;
        const checkerLambdas = formik.values.checkerLambdas;

        PARTICIPATION_LIMIT_SCHEMA.forEach(({ flag, keys }) => {
            if (!limitStates[flag]) {
                deleteKeys(participationLimits, keys);
            }

            if (flag === 'minAge' && limitStates[flag]) {
                checkerLambdas.push(AGE_LIMIT_LAMBDA);
            }
        });

        const useDatesLimit = Object.entries(limitStates).some(
            ([key, value]) => key !== 'minAge' && Boolean(value)
        );

        if (useDatesLimit) {
            formik.values.checkerLambdas.push('participationLimit');

            setFieldValue(
                'participationLimits.participationLimitMultipleChecks',
                true
            );
            convertDatesToTimestamp([
                'participationLimits.participationLimitStartEndDatesRange.startDate',
                'participationLimits.participationLimitStartEndDatesRange.endDate',
                'participationLimits.participationLimitCalendarDatesRange.startDate',
                'participationLimits.participationLimitCalendarDatesRange.endDate',
            ]);
        } else {
            deleteKeys(participationLimits, ['participationLimitMultipleChecks']);
        }
    };

    const transformLanguageCode = () => {
        const language = getIn(formik.values, 'configurationParameters.language');
        const code = Object.keys(languageCodes).find(
            (key) => languageCodes[key] === language
        );
        setFieldValue('configurationParameters.language', code);
    };

    const handleSubmitChanges = () => {
        setFieldValue(
            'configurationId',
            (uniqid() + uniqid()).substring(0, promoIdCharLength)
        );

        setFieldValue (
            'configurationParameters.additionalInformation.name',
            String(
                getIn(
                    formik.values,
                    'configurationParameters.additionalInformation.name'
                )
            )
        );

        handleParticipationLimits();
        transformLanguageCode();
        convertDatesToTimestamp([
            'tempParams.startDateTime',
            'tempParams.endDateTime',
        ]);
        mapTimeZones([
            'configurationParameters.configurationDatesTimezone',
            'participationLimits.participationLimitTimeZone',
            'participationLimits.participationLimitCalendarDatesTimeZone',
        ]);

        formik.submitForm();
        setCompleted(true);
    };

    const location = useLocation();
    const promotionId = location.pathname.replace(/^.*\/(.*)$/, '$1');

    const changesDetected = formik.dirty && formik.submitCount === 0;
    usePrompt(
        'This will discard all the changes. Are you sure?',
        changesDetected
    );


    // Show notification as soon as user lands on the last step and error is present
    useEffect(() => {
        // Determine the last step index for the current flow
        const lastStep = isLuckyNumbers ? 7 : 8;
        const isOnLastStep = step === lastStep;
        const startDateError = Boolean(getIn(errors, 'tempParams.startDateTime'));

        if (isOnLastStep && startDateError) {
            setNotificationState({
                open: true,
                level: 'error',
                title: 'Validation Error',
                content:
                    'Start timestamp is already in the past. Please go back to the Timeline step and update the start date.',
            });
        } else {
            setNotificationState((previousState) =>
                previousState.open ? { ...previousState, open: false } : previousState
            );
        }
    }, [step, errors, isLuckyNumbers]);

    return (
        <>
            {step === 0 && (
                <>
                    <TooltipText
                        text='Promotion Name'
                        textVariant='h4'
                        tooltip='For example, FIFA 2022 Promo Germany.'
                    />
                    <InputComponent
                        inputClassName='text-box'
                        dataTestId='promotion-name-input'
                        formik={formik}
                        inputValue={getIn(
                            formik.values,
                            'configurationParameters.additionalInformation.name'
                        )}
                        formikLabel={'configurationParameters.additionalInformation.name'}
                        label='Promotion Name'
                        text='A unique name gives an identity to your promotion and helps to differentiate it from other promotions that are relevant to the campaign. Once this promotion is created, a unique ID will be automatically assigned to it. You can see the created promotion and its ID under the campaign profile.'
                    />
                </>
            )}
            {step === 1 && (
                <SelectMarketLanguage formik={formik} markets={campaignMarkets} />
            )}
            {step === 2 && (
                <>
                    <TooltipText
                        text='Promotion Dates'
                        textVariant='h4'
                        tooltip='The dates were automatically inherited from the timeline selected for the campaign. If the timeline is different for this promotion, make sure to select the applicable start and end date here.'
                    />
                    <AllocateDates
                        description='Select a start and end date for the overall duration of the promotion'
                        datetime={true}
                        formikStartDate={'tempParams.startDateTime'}
                        formikEndDate={'tempParams.endDateTime'}
                        maxEndDate={
                            getIn(formik.values, 'configurationParameters.configurationEndUtc')
                        }
                        formikTimezoneValue={
                            'configurationParameters.configurationDatesTimezone'
                        }
                        datesClass='date-box'
                        containerSpacing={2}
                        startEndDateColumns={6}
                        timeZoneColumns={12}
                    />
                </>
            )}
            {step === 3 && (
                <Grid container direction='row' justifyContent='center' alignItems='center'>
                    <TooltipText
                        text='Promotion Mechanic'
                        textVariant='h4'
                        tooltip={
                            <Typography variant='body2'>
                                <MuiLink
                                    className='tooltip-link'
                                    href={commonLinks.PROMO_MECHANICS}
                                    target='_blank'
                                >
                                    Learn more
                                </MuiLink>{' '}
                                about NGPS promotion mechanics.
                            </Typography>
                        }
                    />
                    <SelectOption
                        selectClass='text-box'
                        formik={formik}
                        formikValue={'tempParams.flowOption'}
                        optionsList={mechanicOptions}
                        description='Select a suitable promotion mechanic'
                        descriptionVariant='body1'
                        inputLabel={'Promo Mechanic'}
                        customAction={setFlow}
                    />
                    {formik.values.tempParams.flowOption === PromoMechanic.InstantWin && (
                        <Grid item xs={12}>
                            <SelectOption
                                selectClass='text-box'
                                formik={formik}
                                formikValue={'configurationParameters.configurationSubMechanic'}
                                optionsList={[
                                    'Instant Win (Winning Moments)',
                                    'Instant Win (Winning Moments + Guaranteed Prize)',
                                    'Instant Win (Always Win)',
                                ]}
                                inputLabel={'Select Variation'}
                                customAction={checkForAWFlag}
                                dataTestId='instant-win-variation-select'
                            />
                        </Grid>
                    )}
                    {formik.values.tempParams.flowOption === PromoMechanic.InstantWin &&
                        formik.values.configurationParameters.configurationSubMechanic === PromoVariation.InstantWinAW && (
                            <Grid item xs={12} sx={{ mt: 2 }}>
                                <EnableDoubleWinToggle
                                    checked={formik.values.params.doubleWin || false}
                                    onChange={(e) =>
                                        formik.setFieldValue('params.doubleWin', e.target.checked)
                                    }
                                />
                            </Grid>
                        )}
                </Grid>
            )}
            {step === 4 && (isLuckyNumbers ? (
                <LuckyNumbersBatchDetails formik={formik} />
            ) : (
                <>
                    <Typography variant='h4' gutterBottom>
                        Currency for Participation
                    </Typography>
                    <CurrencyDetails formik={formik} />
                </>
            ))}
            {step === 5 && <ProofOfPurchaseDetails formik={formik} />}
            {step === 6 &&
                (formik.values.tempParams.flowOption === 'Instant Win' ||
                    formik.values.tempParams.flowOption === 'Prize Draw (Lottery)' ||
                    isLuckyNumbers) && <ParticipationLimits formik={formik} />}
            {step === 6 &&
                (formik.values.tempParams.flowOption === 'Collect & Get' ||
                    formik.values.tempParams.flowOption === 'Auto Collect & Get') && (
                    <ParticipationSettings formik={formik} />
                )}
            {step === 7 && !isLuckyNumbers && (
                <Form>
                    <TooltipText
                        text='Referral Rewards'
                        textVariant='h4'
                        tooltip='Specify the Referral Rewards for this promotion'
                    />
                    <FriendReferralForm
                        nonFormikCurrList={formik.values?.tempParams?.availableCurrencies?.filter(
                            (currency) =>
                                currency.country === formik.values?.configurationParameters?.country
                        )}
                    />
                </Form>
            )}
            {step === 7 && isLuckyNumbers && (
                completed ? <SavePromotion formik={formik} /> : <AdditionalPromoDetails formik={formik} />
            )}
            {step === 8  && !isLuckyNumbers && (
                <AdditionalPromoDetails formik={formik} />
            )}
            {step > 8  && !isLuckyNumbers && (
                <SavePromotion formik={formik} />
            )}
            {step === 8 && isLuckyNumbers && completed && <SavePromotion formik={formik} />}
            <Box className='buttons' sx={{ mt: 5 }}>
                {step <= 0 && (
                    <Button
                        variant='outlined'
                        component={Link}
                        to={`/editCampaign/${promotionId}`}
                    >
                        Cancel
                    </Button>
                )}
                {step > 0 && step <= (isLuckyNumbers ? (completed ? 7 : 8) : 8) && (
                    <Button
                        color='secondary'
                        variant='contained'
                        startIcon={<ArrowBackIosIcon />}
                        onClick={() => updateStep(-1)}
                    >
                        Previous
                    </Button>
                )}
                {(!isLuckyNumbers && step === 8 && !completed) ||
                 (isLuckyNumbers && step === 7 && !completed) ? (
                    <Button
                        disabled={formHasErrors}
                        variant='contained'
                        onClick={handleSubmitChanges}
                    >
                        Complete
                    </Button>
                ) : null}
                {step < (isLuckyNumbers ? 7 : 8) && (
                    <Button
                        variant='contained'
                        name='Next'
                        endIcon={<ArrowForwardIosIcon />}
                        onClick={() => validateAndMove()}
                    >
                        Next
                    </Button>
                )}
            </Box>
            <Stepper
                activeStep={step}
                alternativeLabel
                sx={{ width: 800, mx: 'auto', mt: 10 }}
            >
                {steps.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>
            <Notification
                notificationState={notificationState}
                setNotificationState={setNotificationState}
            />
        </>
    );
}

export { AssignPromotionForm };
